// File: src/components/fans/DownloadUnlockModal.jsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * DownloadUnlockModal Component
 * A strictly dismissable popup celebrating Offline Streaming Rights unlocked for a track.
 * 
 * Props:
 * - isOpen (boolean): Controls modal visibility
 * - onClose (function): Callback when closing/dismissing the modal
 * - song (object): Target song details { title, artistName, albumArtworkURL, artworkUrl }
 */
const DownloadUnlockModal = ({ isOpen, onClose, song }) => {
  if (!isOpen) return null;

  const artwork = song?.albumArtworkURL || song?.artworkUrl || "/default-artwork.png";
  const title = song?.title || "This Track";
  const artistName = song?.artistName || "Artist";

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black/80 z-[400] flex items-center justify-center p-4 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          className="bg-zinc-900 border border-emerald-500/40 text-white rounded-3xl p-6 md:p-8 w-full max-w-sm text-center relative shadow-[0_0_50px_rgba(16,185,129,0.25)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Track Artwork Banner */}
          <div className="relative w-24 h-24 mx-auto mb-4">
            <img 
              src={artwork} 
              alt={title} 
              className="w-full h-full rounded-2xl object-cover border-2 border-emerald-500 shadow-xl"
            />
            <div className="absolute -bottom-2 -right-2 bg-emerald-500 text-black font-black p-1.5 rounded-full text-xs shadow-md">
              ⚡
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-black italic tracking-tight text-emerald-400 uppercase">
            Download Unlocked!
          </h2>

          <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">
            {title} — {artistName}
          </p>

          {/* Description Box */}
          <div className="my-5 p-4 bg-zinc-800/80 border border-zinc-700/80 rounded-2xl text-xs font-medium text-zinc-300 leading-relaxed space-y-2 text-left">
            <p className="text-zinc-200">
              You’ve officially unlocked <strong className="text-emerald-400">Offline Streaming</strong> for this track.
            </p>
            <p className="text-zinc-400 text-[11px]">
              You can now play this track anytime in your offline player without using network data.
            </p>
          </div>

          {/* Single Dismiss Button */}
          <div className="pt-1">
            <button
              onClick={onClose}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-black py-3.5 px-4 rounded-xl uppercase tracking-wider text-xs shadow-lg transition-all active:scale-95"
            >
              Awesome, Got It
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DownloadUnlockModal;